import { useMemo, useState, type JSX } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { CheckboxField } from '../workspace/ui';
import type { AudienceFolderCount } from './types';

const PURPOSE_LABELS: Record<string, string> = { vyral: 'Vyral', clipping: 'Clipping', influencer: 'Influencer' };

const blank = (publicId: string, name: string): AudienceFolderCount => ({
  publicId,
  name,
  purpose: '',
  archived: false,
  eligible: 0,
  matching: 0,
  kinds: { personal: 0, management: 0, business: 0 },
  faces: { face: 0, faceless: 0, unknown: 0 },
});

/**
 * Every folder with someone you can email, and what's in it: how many
 * addresses, of which email type, and who is on camera. Ticking a folder
 * narrows the audience to it; the counts don't move when you do, because each
 * is worked out as if no folder were picked.
 */
export function FolderPicker({
  folders,
  selected,
  onChange,
  loading,
  nameOf,
}: {
  folders: AudienceFolderCount[];
  selected: string[];
  onChange: (selected: string[]) => void;
  loading: boolean;
  nameOf: (publicId: string) => string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const rows = useMemo(() => {
    // A picked folder can drop to zero under other filters; keep it visible so it can be unticked.
    const known = new Set(folders.map((f) => f.publicId));
    const all = [...folders, ...selected.filter((id) => !known.has(id)).map((id) => blank(id, nameOf(id)))];
    const q = query.trim().toLowerCase();
    return all.filter(
      (f) => selected.includes(f.publicId) || ((showArchived || !f.archived) && (!q || f.name.toLowerCase().includes(q))),
    );
  }, [folders, selected, query, showArchived, nameOf]);

  const archivedCount = folders.filter((f) => f.archived).length;
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Folders</h2>
          <p className="text-sm text-muted-foreground">
            {selected.length
              ? `${selected.length} picked. Only creators in ${selected.length === 1 ? 'that folder' : 'those folders'} are selected.`
              : 'Every folder is included. Tick folders to send to just those.'}{' '}
            Counts use your other filters; a creator in two folders counts in both.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input aria-label="Find a folder" placeholder="Find a folder" className="w-44" value={query} onChange={(ev) => setQuery(ev.target.value)} />
          {archivedCount > 0 && (
            <CheckboxField checked={showArchived} onChange={setShowArchived}>
              Show archived ({archivedCount})
            </CheckboxField>
          )}
          {selected.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
              Clear folders
            </Button>
          )}
        </div>
      </div>
      <div className="max-h-[26rem] overflow-y-auto rounded-xl">
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell className="w-8">
                <span className="sr-only">Pick</span>
              </DataTableHeaderCell>
              <DataTableHeaderCell>Folder</DataTableHeaderCell>
              <DataTableHeaderCell numeric title="Addresses you can email in this folder">Can email</DataTableHeaderCell>
              <DataTableHeaderCell numeric title="Of those, how many your email type, on camera, platform and account type filters select">Selected</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Personal</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Management</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Business</DataTableHeaderCell>
              <DataTableHeaderCell numeric>On camera</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Faceless</DataTableHeaderCell>
              <DataTableHeaderCell numeric title="The on-camera check hasn't run on these">Not checked</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {rows.length === 0 && (
              <DataTableRow>
                <DataTableCell colSpan={10} muted className="py-6 text-center">
                  {loading ? 'Loading folders…' : query ? 'No folder by that name has anyone you can email.' : 'No folder has anyone you can email with these filters.'}
                </DataTableCell>
              </DataTableRow>
            )}
            {rows.map((f) => {
              const on = selected.includes(f.publicId);
              return (
                <DataTableRow key={f.publicId} state={on ? 'selected' : 'default'} className="cursor-pointer" onClick={() => toggle(f.publicId)}>
                  <DataTableCell>
                    <input
                      type="checkbox"
                      aria-label={`Pick ${f.name}`}
                      className="h-4 w-4 rounded border-input accent-[var(--brand)]"
                      checked={on}
                      onChange={() => toggle(f.publicId)}
                      onClick={(ev) => ev.stopPropagation()}
                    />
                  </DataTableCell>
                  <DataTableCell anchor data-label="Folder" className="max-w-[18rem]">
                    <span className="block truncate" title={f.name}>
                      {f.name}
                    </span>
                    <span className="text-sm font-normal text-muted-foreground">
                      {[PURPOSE_LABELS[f.purpose] ?? f.purpose, f.archived ? 'archived' : null].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </DataTableCell>
                  <Count label="Can email" n={f.eligible} strong />
                  <Count label="Selected" n={f.matching} strong={on} />
                  <Count label="Personal" n={f.kinds.personal} />
                  <Count label="Management" n={f.kinds.management} />
                  <Count label="Business" n={f.kinds.business} />
                  <Count label="On camera" n={f.faces.face} />
                  <Count label="Faceless" n={f.faces.faceless} />
                  <Count label="Not checked" n={f.faces.unknown} />
                </DataTableRow>
              );
            })}
          </tbody>
        </DataTable>
      </div>
    </section>
  );
}

function Count({ label, n, strong }: { label: string; n: number; strong?: boolean }): JSX.Element {
  return (
    <DataTableCell numeric data-label={label} muted={n === 0} className={strong ? 'font-semibold' : undefined}>
      {n.toLocaleString()}
    </DataTableCell>
  );
}
